/**
 * SYSTEM INFO DIALOG — Modale read-only che elenca tutti i MessageInfo
 * di sistema di un messaggio (Message.infos, chiave = MessageInfoLabel
 * lato backend).
 *
 * Queste info sono metadati di sistema non di interesse primario nel
 * detail: per questo non vivono più come sezione inline nella
 * MessageCenter ma sono raggiungibili on-demand da un pulsante
 * "Visualizza dettagli" nella sidebar destra (vedi MessageSidebar).
 *
 * A differenza di "Informazioni estratte"/"Informazioni mancanti", qui
 * si mostrano TUTTE le info (anche con value vuoto), ordinate per
 * priority ascendente; il value è sempre read-only.
 *
 * Stile allineato alle altre modali (classi .app-dialog/__header/__body/
 * __footer) e alla tabella info (.message-info-table__label/__value).
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import type { Message } from './messageTypes';

export function SystemInfoDialog({
  message,
  onClose,
}: {
  message: Message;
  onClose: () => void;
}) {
  const { t } = useTranslation();

  // Tutte le info di sistema (value vuoto incluso) ordinate per priority,
  // coerente con la UI delle altre sezioni info.
  const infos = useMemo(
    () => [...message.infos].sort((a, b) => a.priority - b.priority),
    [message.infos],
  );

  // Righe "finte" di testa: gli externalId vivono sul Message stesso, NON sono
  // MessageInfo (niente messageInfoId/priority). Le mostriamo in cima alla
  // stessa tabella, riusando le classi label/value per coerenza visiva.
  const externalRows = [
    { key: 'externalId', label: t('message.systemInfo.externalId'), value: message.externalId },
    { key: 'parentExternalId', label: t('message.systemInfo.parentExternalId'), value: message.parentExternalId },
    { key: 'rootExternalId', label: t('message.systemInfo.rootExternalId'), value: message.rootExternalId },
  ];

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth className="app-dialog system-info-dialog">
      <DialogTitle className="app-dialog__header">
        <span className="app-dialog__title">{t('message.systemInfo.title')}</span>
        <IconButton
          aria-label={t('common.close')}
          onClick={onClose}
          size="small"
          className="app-dialog__close"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent className="app-dialog__body">
        <Table size="small">
          <TableBody>
            {externalRows.map((row) => (
              <TableRow key={row.key} className="message-info-table__row--external">
                <TableCell className="message-info-table__label">{row.label}</TableCell>
                <TableCell className="message-info-table__value">{row.value || '—'}</TableCell>
              </TableRow>
            ))}
            {infos.map((info) => (
              <TableRow key={info.messageInfoId}>
                <TableCell className="message-info-table__label">
                  {info.labelTranslated || info.label}
                </TableCell>
                <TableCell className="message-info-table__value">{info.value}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {infos.length === 0 && (
          <p className="text-muted">{t('message.systemInfo.empty')}</p>
        )}
      </DialogContent>
      <DialogActions className="app-dialog__footer">
        <Button onClick={onClose} variant="outlined">
          {t('common.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
